import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/motion/Reveal";
import { SectionIntro } from "@/components/sections/SectionIntro";
import { DIVISIONS } from "@/content/company";

const Areas = ({ areas }) => (
  <ul className="mt-6 flex flex-wrap gap-2">
    {areas.map((a) => (
      <li key={a} className="rounded-full border border-white/15 px-3 py-1.5 text-[12px] text-white/80">{a}</li>
    ))}
  </ul>
);

const DivisionCard = ({ d, large }) => (
  <article
    className="group relative flex h-full flex-col overflow-hidden rounded-[28px] border border-white/10 bg-ink-2 transition-[border-color,transform] duration-700 hover:-translate-y-1 hover:border-gold/50"
    data-testid={`division-card-${d.key}`}
  >
    <div className={`relative overflow-hidden ${large ? "h-64 sm:h-80 lg:h-auto lg:min-h-[340px] lg:flex-1" : "h-48 sm:h-56"}`}>
      <img src={d.image} alt={d.alt} loading="lazy" className="absolute inset-0 h-full w-full object-cover grayscale-[35%] transition-[transform,filter] duration-1000 ease-out group-hover:scale-105 group-hover:grayscale-0" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink-2 via-ink-2/30 to-plum/20" />
      <span className="absolute left-6 top-6 rounded-full border border-gold/40 bg-ink/70 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.2em] text-gold-light backdrop-blur-sm">
        {d.index} — {d.badge}
      </span>
    </div>
    <div className={`flex flex-col p-7 sm:p-9 ${large ? "" : "flex-1"}`}>
      <h3 className={`font-display font-light tracking-tight text-white ${large ? "text-4xl sm:text-5xl" : "text-3xl sm:text-4xl"}`}>{d.name}</h3>
      <p className="mt-4 max-w-xl text-[15px] leading-relaxed text-white/75 md:text-base">{d.summary}</p>
      <p className="eyebrow mt-8 text-white/60">Key areas</p>
      <Areas areas={d.areas} />
      <div className="mt-auto pt-9 lg:pt-10">
        <Link to={d.to} data-testid={`division-cta-${d.key}`} className="inline-flex min-h-[48px] items-center gap-3 rounded-full border border-gold/50 px-6 text-[12px] font-semibold uppercase tracking-[0.16em] text-gold-light transition-colors duration-500 hover:bg-gold hover:text-ink">
          {d.cta}
          <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:rotate-45" />
        </Link>
      </div>
    </div>
  </article>
);

export const Ecosystem = () => {
  const [academy, solutions, consult] = DIVISIONS;
  return (
    <section id="ecosystem" className="grain relative scroll-mt-20 overflow-hidden bg-plum-deep py-24 sm:py-32" data-testid="home-ecosystem">
      <div aria-hidden="true" className="pointer-events-none absolute right-[-15%] top-20 h-[600px] w-[600px] rounded-full bg-plum-3/30 blur-[140px]" />
      <div className="shell relative z-[2]">
        <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
          <SectionIntro eyebrow="Our Ecosystem" title={<>One Vision. <span className="italic text-gold-metal">Three Powerful</span> Divisions.</>} />
          <Reveal as="p" delay={0.1} className="max-w-sm text-base leading-relaxed text-white/70">
            VION Tech is the umbrella brand. Each division focuses on a distinct part of the journey — learning, transforming and growing.
          </Reveal>
        </div>
        <div className="mt-16 grid gap-6 lg:grid-cols-12 lg:grid-rows-2">
          <Reveal className="lg:col-span-7 lg:row-span-2"><DivisionCard d={academy} large /></Reveal>
          <Reveal delay={0.1} className="lg:col-span-5"><DivisionCard d={solutions} /></Reveal>
          <Reveal delay={0.2} className="lg:col-span-5"><DivisionCard d={consult} /></Reveal>
        </div>
      </div>
    </section>
  );
};
